import { inject, Injectable } from '@angular/core';
import {
  ConnectedDataSource,
  DateRangePreset,
  FilterComponent,
  FilterConfig,
  FilterData,
  FilterOption,
  fullNameKey,
  idKey,
  nameKey,
} from '@tiltgroup/web-components';
import { SqlOperator } from '@graphql-types';
import { ConnectedDataSourcesService } from '@services/connected-data-sources.service';
import {
  documentMimeTypeOptions,
  documentSourceOptions,
  documentTypeOptions,
  loadStatuses,
  ltlQuoteEquipmentTypes,
  ltlQuotePayTypes,
  ltlQuoteShipTypes,
  quoteStatus,
  trashedFilters,
} from '@shared/constants';

export enum FilterKey {
  ID = 'id',
  ORIGIN = 'origin',
  ORIGIN_STATE = 'origin_state',
  DESTINATION = 'destination',
  DESTINATION_STATE = 'destination_state',
  EQUIPMENT_TYPE = 'equipment_type',
  CREATED_AT = 'created_at',
  CREATED_BY = 'created_by',
  UPDATED_AT = 'updated_at',
  UPDATED_BY = 'updated_by',
  DELETED_AT = 'deleted_at',
  DELETED_BY = 'deleted_by',
  TRASHED = 'trashed',
  QUOTE_STATUS = 'quote_status',
  SHIPMENT_STATUS = 'shipment_status',
  MBOL_NUMBER = 'mbol_number',
  ESTIMATED_PICKUP_AT = 'estimated_pickup_at',
  SHIP_TYPE = 'ship_type',
  PAY_TYPE = 'pay_type',
  BOL_NUMBER = 'bol_number',
  PICKUP_NUMBER = 'pickup_number',
  PRO_NUMBER = 'pro_number',
  QUOTE_NUMBER = 'quote_number',
  IS_QUICK_QUOTE = 'isQuickQuote',
  CONTAINS_HAZARDOUS_MATERIALS = 'containsHazardousMaterials',
  ORIGIN_CITY = 'origin_city',
  DESTINATION_CITY = 'destination_city',
  PICKUP_AT = 'pickup_at',
  DELIVERY_AT = 'delivery_at',
  TRACKING_STARTED = 'tracking_started',
  FREIGHT_CLASS = 'freight_class',
  FREIGHT_TRANSPORTATION_MODE = 'freight_transportation_mode',
  FOOTAGE = 'footage',
  PIECES = 'pieces',
  WEIGHT = 'weight',
  TRUCK_NUMBER = 'truck_number',
  TRAILER_NUMBER = 'trailer_number',
  ELD = 'eld',
  VIN = 'vin',
  TARP = 'tarp',
  TRUCK_MILES = 'truck_miles',
  ITRACKER_ID = 'itracker_id',
  IS_CUSTOMER_PAID = 'is_customer_paid',
  CONTAINER_NUMBER = 'container_number',
  SEAL_NUMBER = 'seal_number',
  VESSEL_NUMBER = 'vessel_number',
  VOYAGE_NUMBER = 'voyage_number',
  BOOKING_NUMBER = 'booking_number',
  DELIVERY_TYPE = 'delivery_type',
  DRAYAGE_DELIVERY_MODE = 'drayage_delivery_mode',
  CONTAINER_ARRIVAL_LOCATION = 'container_arrival_location',
  OCEAN_CARRIER_IDENTIFIER = 'ocean_carrier_identifier',
  ORIGIN_POSTCODE = 'origin_postcode',
  DESTINATION_POSTCODE = 'destination_postcode',
  ORIGIN_FACILITY_NAME = 'origin_facility_name',
  DESTINATION_FACILITY_NAME = 'destination_facility_name',
  ORIGIN_APPOINTMENT_TYPE = 'origin_appointment_type',
  DESTINATION_APPOINTMENT_TYPE = 'destination_appointment_type',
  TEAM_NAME = 'team_name',
  ETA_AT_UTC = 'eta_at_utc',
  RELEASED_AT_UTC = 'released_at_utc',
  INVOICED_AT_UTC = 'invoiced_at_utc',
  CARRIER_INVOICED_AT_UTC = 'carrier_invoiced_at_utc',
  CARRIER_PAID_AT_UTC = 'carrier_paid_at_utc',
  PAID_AT_UTC = 'paid_at_utc',
  GATE_OUT_AT_UTC = 'gate_out_at_utc',
  GATE_IN_AT_UTC = 'gate_in_at_utc',
  LAST_FREE_DAY_AT_UTC = 'last_free_day_at_utc',
  AVAILABLE_FOR_PICKUP_AT_UTC = 'available_for_pickup_at_utc',
  AT_FINAL_DESTINATION_PORT_AT_UTC = 'at_final_destination_port_at_utc',
  LEFT_LOADING_PORT_AT_UTC = 'left_loading_port_at_utc',
  ETA_PORT_AT_UTC = 'eta_port_at_utc',
  DELIVERED_AT_UTC = 'delivered_at_utc',
  DROPPED_ON_YARD_AT_UTC = 'dropped_on_yard_at_utc',
  READY_ON_YARD_AT_UTC = 'ready_on_yard_at_utc',
  PICKED_UP_FROM_YARD_AT_UTC = 'picked_up_from_yard_at_utc',
  NAME = 'name',
  TYPE = 'type',
  MIME_TYPE = 'mime_type',
  SOURCE = 'source',
}

@Injectable({
  providedIn: 'root',
})
export class TableFiltersService {
  private _connectedDataSourcesService = inject(ConnectedDataSourcesService);

  filterOperators = {
    [FilterComponent.INPUT]: [
      SqlOperator.Ilike,
      SqlOperator.NotIlike,
      SqlOperator.StartsWith,
      SqlOperator.EndWith,
      SqlOperator.IsNotNull,
      SqlOperator.IsNull,
    ],
    [FilterComponent.NUMBER]: [
      SqlOperator.Eq,
      SqlOperator.Neq,
      SqlOperator.Gt,
      SqlOperator.Gte,
      SqlOperator.Lt,
      SqlOperator.Lte,
      SqlOperator.IsNotNull,
      SqlOperator.IsNull,
    ],
    [FilterComponent.CHECKLIST]: [
      SqlOperator.In,
      SqlOperator.NotIn,
      SqlOperator.IsNotNull,
    ],
    [FilterComponent.CHIP_BOX]: [
      SqlOperator.In,
      SqlOperator.NotIn,
      SqlOperator.IsNotNull,
    ],
  };

  excludeNullFilters = (filters: SqlOperator[]) =>
    filters.filter(
      (filter) =>
        filter != SqlOperator.IsNotNull && filter != SqlOperator.IsNull
    );

  get IDFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.INPUT]
      ),
      inputPlaceholder: 'Type id',
      title: 'ID',
      icon: 'nx:ic-number',
      key: FilterKey.ID,
    };
  }

  get IDDefaultFilter(): FilterData {
    return { ...this.IDFilter, default: true };
  }

  get originFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      inputPlaceholder: 'Type origin',
      icon: 'nx:ic-origin',
      title: 'Origin',
      key: FilterKey.ORIGIN,
    };
  }

  get destinationFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      icon: 'nx:ic-destination',
      inputPlaceholder: 'Type destination',
      title: 'Destination',
      key: FilterKey.DESTINATION,
    };
  }

  get ltlEquipmentTypeFilter(): FilterData {
    return {
      type: FilterComponent.CHECKLIST,
      title: 'Equipment type',
      icon: 'nx:ic-equipment-type',
      key: FilterKey.EQUIPMENT_TYPE,
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.CHIP_BOX]
      ),
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: ltlQuoteEquipmentTypes,
      },
    };
  }

  get trashedFilter(): FilterData {
    return {
      type: FilterComponent.RADIO_GROUP,
      logicalOperators: [],
      icon: 'nx:ic-delete',
      title: 'Show archived?',
      key: FilterKey.TRASHED,
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: trashedFilters,
      },
      panelHeight: 130,
    };
  }

  get createdAtFilter(): FilterData {
    return this.calendarRangeFilter('Created at', FilterKey.CREATED_AT);
  }

  get createdByFilter(): FilterData {
    return this.operationByFilter('Created by', FilterKey.CREATED_BY);
  }

  get updatedAtFilter(): FilterData {
    return this.calendarRangeFilter('Updated at', FilterKey.UPDATED_AT);
  }

  get deletedAtFilter(): FilterData {
    return this.calendarRangeFilter('Deleted at', FilterKey.DELETED_AT);
  }

  get documentNameFilter(): FilterData {
    return this.inputFilter('Name', FilterKey.NAME, 'nx:ic-file');
  }

  get documentTypeFilter(): FilterData {
    return this.checklistFilter('Type', FilterKey.TYPE, documentTypeOptions);
  }

  get documentExtensionFilter(): FilterData {
    return this.checklistFilter(
      'Format',
      FilterKey.MIME_TYPE,
      documentMimeTypeOptions,
      'nx:ic-file'
    );
  }

  get documentSourceFilter(): FilterData {
    return this.checklistFilter(
      'Source',
      FilterKey.SOURCE,
      documentSourceOptions
    );
  }

  get quoteStatusFilter(): FilterData {
    return {
      type: FilterComponent.CHECKLIST,
      title: 'Status',
      icon: 'nx:ic-pending-s',
      iconClass: 'twc-color-secondary-typeface',
      key: FilterKey.QUOTE_STATUS,
      logicalOperators: this.filterOperators[FilterComponent.CHIP_BOX],
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: quoteStatus,
      },
    };
  }

  get shipmentStatusFilter(): FilterData {
    return {
      type: FilterComponent.CHECKLIST,
      title: 'Status',
      icon: 'nx:ic-pending-s',
      iconClass: 'twc-color-secondary-typeface',
      key: FilterKey.SHIPMENT_STATUS,
      logicalOperators: this.filterOperators[FilterComponent.CHIP_BOX],
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: loadStatuses,
      },
    };
  }

  get mbolNumberFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      inputPlaceholder: 'Type MBOL #',
      title: 'MBOL #',
      key: FilterKey.MBOL_NUMBER,
    };
  }

  get estimatedPickUpAtFilter(): FilterData {
    return this.calendarRangeFilter(
      'Shipping date',
      FilterKey.ESTIMATED_PICKUP_AT
    );
  }

  get shipTypeFilter(): FilterData {
    return {
      type: FilterComponent.CHECKLIST,
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.CHIP_BOX]
      ),
      inputPlaceholder: 'Type ship type',
      title: 'Ship type',
      icon: 'nx:ic-unknown',
      key: FilterKey.SHIP_TYPE,
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: ltlQuoteShipTypes,
      },
    };
  }

  get payTypeFilter(): FilterData {
    return {
      type: FilterComponent.CHECKLIST,
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.CHIP_BOX]
      ),
      inputPlaceholder: 'Type pay type',
      title: 'Pay type',
      icon: 'nx:ic-unknown',
      key: FilterKey.PAY_TYPE,
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: ltlQuotePayTypes,
      },
    };
  }

  get bolNumberFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      inputPlaceholder: 'Type BOL #',
      title: 'BOL #',
      key: FilterKey.BOL_NUMBER,
    };
  }

  get pickupNumberFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      inputPlaceholder: 'Type pickup #',
      title: 'Pickup #',
      key: FilterKey.PICKUP_NUMBER,
    };
  }

  get proNumberFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      inputPlaceholder: 'Type pro #',
      title: 'Pro #',
      key: FilterKey.PRO_NUMBER,
    };
  }

  get quoteNumberFilter(): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      inputPlaceholder: 'Type quote #',
      title: 'Quote #',
      key: FilterKey.QUOTE_NUMBER,
    };
  }

  get isQuickQuote(): FilterData {
    return {
      type: FilterComponent.RADIO_GROUP,
      title: 'Is quick quote',
      icon: 'nx:ic-tracking-started',
      key: FilterKey.IS_QUICK_QUOTE,
      logicalOperators: [SqlOperator.Eq, SqlOperator.Neq],
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: [
          { id: 'true', name: 'Yes' },
          { id: 'false', name: 'No' },
        ],
      },
    };
  }

  get containsHazardousMaterials(): FilterData {
    return {
      type: FilterComponent.RADIO_GROUP,
      title: 'Contains hazardous materials',
      icon: 'nx:ic-unknown',
      key: FilterKey.CONTAINS_HAZARDOUS_MATERIALS,
      logicalOperators: [SqlOperator.Eq],
      panelHeight: 90,
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: [
          { id: 'true', name: 'Yes' },
          { id: 'false', name: 'No' },
        ],
      },
    };
  }

  calendarRangeFilter(
    title: string,
    key: FilterKey,
    config: FilterConfig = {
      calendarConfig: {
        presets: [
          DateRangePreset.TODAY,
          DateRangePreset.YESTERDAY,
          DateRangePreset.YESTERDAY_TODAY,
          DateRangePreset.LAST_7_DAYS,
          DateRangePreset.LAST_30_DAYS,
        ],
      },
    }
  ): FilterData {
    return {
      type: FilterComponent.CALENDAR_RANGE,
      title,
      icon: 'nx:ic-calendar',
      key: key,
      config,
    };
  }

  get teamNameFilter(): FilterData {
    return {
      type: FilterComponent.CHIP_BOX,
      title: 'Team',
      key: FilterKey.TEAM_NAME,
      icon: 'nx:ic-prospect',
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.CHIP_BOX]
      ),
      data: {
        keyPath: 'id',
        namePath: nameKey,
        dataSource: this._connectedDataSourcesService
          .teamsDataSource as ConnectedDataSource,
      },
    };
  }

  operationByFilter(title: string, key: FilterKey): FilterData {
    return {
      type: FilterComponent.CHIP_BOX,
      title,
      key,
      icon: 'nx:ic-prospect',
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.CHIP_BOX]
      ),
      data: {
        keyPath: 'id',
        namePath: fullNameKey,
        dataSource: this._connectedDataSourcesService
          .userDataSource as ConnectedDataSource,
      },
    };
  }

  inputFilter(
    title: string,
    key: FilterKey,
    icon = 'nx:ic-number'
  ): FilterData {
    return {
      type: FilterComponent.INPUT,
      logicalOperators: this.filterOperators[FilterComponent.INPUT],
      inputPlaceholder: `Type ${title.toLowerCase()}`,
      title,
      icon,
      key,
    };
  }

  numberFilter(title: string, key: FilterKey): FilterData {
    return {
      type: FilterComponent.NUMBER,
      logicalOperators: this.filterOperators[FilterComponent.NUMBER],
      inputPlaceholder: `Type ${title.toLowerCase()}`,
      title,
      icon: 'nx:ic-number',
      key,
    };
  }

  checklistFilter(
    title: string,
    key: FilterKey,
    options: FilterOption[],
    icon = 'nx:ic-unknown'
  ): FilterData {
    return {
      type: FilterComponent.CHECKLIST,
      title,
      icon,
      key,
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.CHIP_BOX]
      ),
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options,
      },
    };
  }

  chipBoxFilter(
    title: string,
    key: FilterKey,
    options: FilterOption[],
    icon = 'nx:ic-unknown'
  ): FilterData {
    return {
      type: FilterComponent.CHIP_BOX,
      title,
      icon,
      key,
      logicalOperators: this.excludeNullFilters(
        this.filterOperators[FilterComponent.CHIP_BOX]
      ),
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options,
      },
    };
  }

  yesNoRadioFilter(title: string, key: FilterKey): FilterData {
    return {
      type: FilterComponent.RADIO_GROUP,
      title,
      icon: 'nx:ic-unknown',
      key,
      logicalOperators: [SqlOperator.Eq],
      panelHeight: 90,
      data: {
        keyPath: idKey,
        namePath: nameKey,
        options: [
          { id: 'true', name: 'Yes' },
          { id: 'false', name: 'No' },
        ],
      },
    };
  }
}
